/* ZoeFeedbackMount.tsx — Zoe-specific glue for the reusable feedback toolbar. */

import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FeedbackToolbar } from './FeedbackToolbar';
import { ScreenNavigator } from './ScreenNavigator';
import { buildZoeScreens } from './zoe-screens';
import { API_BASE } from '../lib/api-url';
import { sessionRequestHeaders } from '../lib/session-headers';
import { useMe } from '../lib/auth/useMe';
import './feedback-toolbar.css';

/** The feedback bar is 44px. */
function useDebugBarHeight(px: string) {
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.style.getPropertyValue('--zoe-debug-bar-height');
    root.style.setProperty('--zoe-debug-bar-height', px);
    return () => {
      if (prev) root.style.setProperty('--zoe-debug-bar-height', prev);
      else root.style.removeProperty('--zoe-debug-bar-height');
    };
  }, [px]);
}

/** Flip to false to ship just the core feedback toolbar with no screen catalog. */
const ENABLE_SCREEN_NAVIGATOR = true;

/** QA/demo chrome. */
function qaChromeEnabled(isZoeAdmin: boolean): boolean {
  if (typeof window === 'undefined') return false;
  let flag: string | undefined;
  let optedOut = false;
  try {
    flag = import.meta.env?.VITE_QA_CHROME;
    optedOut = window.localStorage.getItem('zoe_qa_chrome') === 'off';
  } catch {
    // localStorage can throw (Safari private mode, blocked storage).
  }
  if (flag === '0' || optedOut) return false;
  if (import.meta.env?.PROD === true) return flag === 'on' || isZoeAdmin;
  return true;
}

/**
 * External-facing token routes served to anonymous recipients (share, invite,
 * survey, follow-up, join and magic-link links). The toolbar never mounts here,
 * even for a Zoe admin, so no screenshot or ticket can carry the token.
 */
const EXTERNAL_TOKEN_ROUTE_PREFIXES = [
  '/share/',
  '/invite/',
  '/survey/',
  '/founder-followup/',
  '/join/',
  '/magic-link',
];

function isExternalTokenRoute(pathname: string): boolean {
  return EXTERNAL_TOKEN_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/** Turn "/deal/ACME/report" into "Deal · ACME · report" for the meta chip. */
function labelForPath(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return 'Home';
  return parts
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' · ');
}

export function ZoeFeedbackMount() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const externalTokenRoute = isExternalTokenRoute(pathname);
  // An expired session must not redirect away while a sign-in link redeems.
  // These pages never show the toolbar, so they do not need its session probe.
  const { me } = useMe({ enabled: !externalTokenRoute });
  const enabled = qaChromeEnabled(me?.is_zoe_admin === true) && !externalTokenRoute;
  // No feedback bar when disabled → content must not reserve 44px for it.
  useDebugBarHeight(enabled ? '44px' : '0px');
  const screens = useMemo(() => buildZoeScreens(pathname), [pathname]);
  // Stable identity so the provider's sync effects do not restart each render.
  const authHeaders = useCallback((): Record<string, string> => {
    return sessionRequestHeaders();
  }, []);

  if (!enabled) return null;
  return (
    <>
      {/* Core feedback toolbar — always on. */}
      <FeedbackToolbar
        route={pathname}
        routeLabel={labelForPath(pathname)}
        brand="Zoe · Feedback"
        syncUrl={`${API_BASE}/api/feedback/tickets`}
        /* Viewport captures, stored one row per image so the ticket collection stays small. */
        screenshotUrl={`${API_BASE}/api/feedback/screenshots`}
        authHeaders={authHeaders}
        isZoeAdmin={me?.is_zoe_admin === true}
        audienceKey={me?.user_id ?? 'unauthenticated'}
        offsetBody={false}
        onNavigate={(route) => navigate(route)}
      />
      {/* Optional, separable add-on — remove this line and the core is untouched. */}
      {ENABLE_SCREEN_NAVIGATOR && (
        <ScreenNavigator screens={screens} route={pathname} onNavigate={(route) => navigate(route)} />
      )}
    </>
  );
}
